import React, { useId } from 'react';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  CreditCard,
  Shield,
  Loader,
  AlertCircle,
  Briefcase,
  Wallet,
  Check,
  Rocket,
  Award,
  Scale,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  PartyPopper,
  PiggyBank,
} from 'lucide-react';

import Card from './common/Card';
import StatCard from './common/StatCard';
import SafeChart from './common/SafeChart';
import GlowArea from './charts/GlowArea';
import GlowBars from './charts/GlowBars';
import GlowDonut from './charts/GlowDonut';
import ChartDefs from './charts/ChartDefs';
import useChartTheme from './charts/useChartTheme';
import { makeChartIds, lighten } from './charts/chartTokens';

/* tone to soft-chip + text token, for the health-summary icons. */
const TONE = {
  pos: { chip: 'bg-arc-pos-soft', text: 'text-arc-pos' },
  accent: { chip: 'bg-arc-accent-soft', text: 'text-arc-accent-ink' },
  warn: { chip: 'bg-arc-warn-soft', text: 'text-arc-warn' },
  neg: { chip: 'bg-arc-neg-soft', text: 'text-arc-neg' },
};

/**
 * Dashboard overview – combines save-status banner, KPI cards and enhanced charts.
 * Now includes auto-calculated metrics and improved visualizations.
 */
const OverviewTab = ({
  /* save banner */
  saving,
  lastSaved,
  saveUserData,
  error,
  /* stats */
  incomeData,
  netWorth,
  totalDebts,
  totalInvestments,
  emergencyFund,
  /* charts */
  netIncomeData,
  goals,
  investments,
  debts,
  expenseData,
  /* on-chain wallets */
  wallets = [],
  /* chartsReady flag from parent */
  chartsReady,
}) => {
  // Total crypto value across all connected wallets.
  // Uses manual override value when set, falls back to API-fetched value.
  const totalCryptoHoldings = wallets.reduce(
    (s, w) =>
      s +
      (typeof w.manualValue === "number" && !isNaN(w.manualValue)
        ? w.manualValue
        : w.lastValue || 0),
    0
  );

  // Net worth includes wallet holdings on top of manual investments
  const autoNetWorth = totalInvestments + totalCryptoHoldings - totalDebts;
  const emergencyFundFromInvestments = investments
    .filter(inv => inv.type === 'Emergency Fund')
    .reduce((sum, inv) => sum + inv.amount, 0);
  
  // Calculate expense average for emergency fund target
  const calculateExpenseAverage = (months = 6) => {
    if (!expenseData.length) return 0;
    const recentExpenses = expenseData.slice(-months);
    const totalExpenses = recentExpenses.reduce((sum, row) => sum + (row.total || 0), 0);
    return recentExpenses.length > 0 ? totalExpenses / recentExpenses.length : 0;
  };

  const emergencyFundTarget = calculateExpenseAverage(6) * 6;
  const emergencyFundProgress = emergencyFundTarget ? (emergencyFundFromInvestments / emergencyFundTarget) * 100 : 0;

  // Prepare investment category data for pie chart
  const getInvestmentByCategory = () => {
    const categoryTotals = {};
    investments.forEach(inv => {
      const category = inv.type || 'Uncategorized';
      categoryTotals[category] = (categoryTotals[category] || 0) + (inv.amount || 0);
    });
    
    return Object.entries(categoryTotals)
      .map(([name, amount]) => ({ name, amount }))
      .filter(item => item.amount > 0)
      .sort((a, b) => b.amount - a.amount);
  };

  const investmentCategories = getInvestmentByCategory();

  // Get recent months data for trends
  const recentMonths = netIncomeData.slice(-6);

  // Filter goals for display (exclude auto-calculated ones or show calculated values)
  const displayGoals = goals.map(goal => {
    if (goal.name === 'FIRE Target') {
      return { ...goal, current: autoNetWorth, progress: goal.target ? (autoNetWorth / goal.target) * 100 : 0 };
    }
    if (goal.name === 'Emergency Fund') {
      return { 
        ...goal, 
        current: emergencyFundFromInvestments,
        target: emergencyFundTarget,
        progress: emergencyFundProgress
      };
    }
    return goal;
  });

  // Currency formatter — max 2 decimal places, consistently (never 3).
  const fmt = (n) => Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });

  // Goals progress bars reuse the chart glow system: the same arcGlow filter +
  // gold/pos tokens as the Overview charts (via ChartDefs/useChartTheme/chartTokens),
  // so they match the charts and dial the glow back in light mode automatically.
  const glow = useChartTheme();
  const glowIds = makeChartIds(useId());
  const goalFillStyle = (progress) => {
    const tone = progress >= 100 ? 'pos' : 'gold'; // keep the >=100% green switch
    return {
      background: `linear-gradient(180deg, ${lighten(glow.tone[tone], 0.45)}, ${glow.tone[tone]})`,
      filter: `url(#${glowIds.glow})`,
    };
  };

  // Health-summary status (icon + label + tone), derived from the same thresholds as before.
  const wealthTone =
    autoNetWorth >= 1000000 ? 'pos' : autoNetWorth >= 100000 ? 'accent' : autoNetWorth >= 0 ? 'warn' : 'neg';
  const wealthStatus =
    autoNetWorth >= 1000000 ? { Icon: Rocket, label: 'Millionaire' }
    : autoNetWorth >= 500000 ? { Icon: Award, label: 'Half Millionaire' }
    : autoNetWorth >= 100000 ? { Icon: TrendingUp, label: 'Six Figures' }
    : autoNetWorth >= 0 ? { Icon: Scale, label: 'Positive Net Worth' }
    : { Icon: AlertTriangle, label: 'Negative Net Worth' };

  const emergencyTone =
    emergencyFundProgress >= 100 ? 'pos' : emergencyFundProgress >= 50 ? 'warn' : 'neg';
  const emergencyStatus =
    emergencyFundProgress >= 100 ? { Icon: CheckCircle2, label: 'Fully Prepared' }
    : emergencyFundProgress >= 50 ? { Icon: AlertTriangle, label: 'Partially Ready' }
    : { Icon: XCircle, label: 'Needs Attention' };

  const debtTone = totalDebts === 0 ? 'pos' : totalDebts < 50000 ? 'warn' : 'neg';
  const debtStatus =
    totalDebts === 0 ? { Icon: PartyPopper, label: 'Debt Free' }
    : totalDebts < 50000 ? { Icon: TrendingDown, label: 'Manageable' }
    : { Icon: AlertTriangle, label: 'High Debt' };

  return (
    <div className="space-y-6">
      {/* Save Status */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div
          className={`flex-1 p-3 rounded-arc-ctl ring-1 ring-arc-line ${
            saving ? 'bg-arc-warn-soft' : 'bg-arc-pos-soft'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {saving ? (
                <>
                  <Loader className="animate-spin text-arc-warn" size={16} />
                  <span className="text-sm text-arc-warn">Saving changes…</span>
                </>
              ) : (
                <>
                  <Check className="text-arc-pos" size={16} />
                  <span className="text-sm text-arc-pos">
                    Auto‑saved {lastSaved ? lastSaved.toLocaleTimeString() : 'recently'}
                  </span>
                </>
              )}
            </div>

            <button
              onClick={saveUserData}
              disabled={saving}
              className="text-sm text-arc-accent-ink hover:brightness-110 font-semibold disabled:opacity-50"
            >
              Save Now
            </button>
          </div>
        </div>

        {error && (
          <div className="flex-1 p-3 bg-arc-neg-soft ring-1 ring-arc-line rounded-arc-ctl">
            <div className="flex items-center gap-2">
              <AlertCircle className="text-arc-neg" size={16} />
              <span className="text-sm text-arc-neg">{error}</span>
            </div>
          </div>
        )}
      </div>

      {/* Enhanced Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard
          title="Latest Income"
          value={incomeData[incomeData.length - 1]?.total || 0}
          icon={DollarSign}
          color="green"
          subtitle="This month"
        />
        <StatCard
          title="Net Worth"
          value={autoNetWorth}
          icon={TrendingUp}
          color={autoNetWorth >= 0 ? "blue" : "red"}
          subtitle="Auto-calculated"
        />
        <StatCard
          title="Total Investments"
          value={totalInvestments}
          icon={Briefcase}
          color="purple"
          subtitle="Manual entries"
        />
        <StatCard
          title="Crypto Holdings"
          value={totalCryptoHoldings}
          icon={Wallet}
          color="yellow"
          subtitle={
            wallets.length === 0
              ? "No wallets yet"
              : `${wallets.length} wallet${wallets.length === 1 ? "" : "s"} connected`
          }
        />
        <StatCard
          title="Total Debt"
          value={totalDebts}
          icon={CreditCard}
          color="red"
          subtitle="Outstanding"
        />
        <StatCard
          title="Emergency Fund"
          value={emergencyFundFromInvestments}
          icon={Shield}
          color="yellow"
          subtitle={`${emergencyFundProgress.toFixed(0)}% of target`}
        />
      </div>

      {/* Financial Health Summary */}
      <Card hover={false}>
        <h3 className="text-h2 text-arc-ink mb-4">Financial Health Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Wealth Building */}
          <div className="text-center">
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-3 ${TONE[wealthTone].chip}`}>
              <TrendingUp className={`w-8 h-8 ${TONE[wealthTone].text}`} />
            </div>
            <h4 className="font-semibold mb-1 text-arc-ink">Wealth Status</h4>
            <p className="text-sm text-arc-ink-3 flex items-center justify-center gap-1.5">
              <wealthStatus.Icon size={15} className={TONE[wealthTone].text} />
              {wealthStatus.label}
            </p>
          </div>

          {/* Emergency Preparedness */}
          <div className="text-center">
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-3 ${TONE[emergencyTone].chip}`}>
              <Shield className={`w-8 h-8 ${TONE[emergencyTone].text}`} />
            </div>
            <h4 className="font-semibold mb-1 text-arc-ink">Emergency Ready</h4>
            <p className="text-sm text-arc-ink-3 flex items-center justify-center gap-1.5">
              <emergencyStatus.Icon size={15} className={TONE[emergencyTone].text} />
              {emergencyStatus.label}
            </p>
          </div>

          {/* Debt Status */}
          <div className="text-center">
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-3 ${TONE[debtTone].chip}`}>
              <CreditCard className={`w-8 h-8 ${TONE[debtTone].text}`} />
            </div>
            <h4 className="font-semibold mb-1 text-arc-ink">Debt Status</h4>
            <p className="text-sm text-arc-ink-3 flex items-center justify-center gap-1.5">
              <debtStatus.Icon size={15} className={TONE[debtTone].text} />
              {debtStatus.label}
            </p>
          </div>
        </div>
      </Card>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Income vs Expenses Trend (Recent 6 months) */}
        <Card hover={false}>
          <h3 className="text-h2 text-arc-ink mb-4">Recent Income vs Expenses Trend</h3>
          <SafeChart chartsReady={chartsReady}>
            <GlowArea
              data={recentMonths}
              xKey="month"
              series={[
                { key: 'income', name: 'Income', tone: 'pos' },
                { key: 'expenses', name: 'Expenses', tone: 'neg' },
                { key: 'netIncome', name: 'Net Income', tone: 'gold' },
              ]}
              yTickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              valueFormatter={(v) => `$${Number(v).toLocaleString()}`}
            />
          </SafeChart>
        </Card>

        {/* Goal Progress with Auto-Updates */}
        <Card hover={false}>
          <h3 className="text-h2 text-arc-ink mb-4">Financial Goals Progress</h3>
          <ChartDefs ids={glowIds} colors={glow} />
          <div className="space-y-4">
            {displayGoals.slice(0, 4).map((goal) => (
              <div key={goal.name}>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-arc-ink">{goal.name}</span>
                  <span className="text-sm tnum text-arc-ink-3">{goal.progress.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-arc-paper-2 rounded-full h-3">
                  <div
                    className="h-3 rounded-full transition-all duration-300"
                    style={{
                      width: `${Math.min(goal.progress, 100)}%`,
                      ...goalFillStyle(goal.progress),
                    }}
                  />
                </div>
                <div className="flex justify-between text-xs tnum text-arc-ink-4 mt-1">
                  <span>${fmt(goal.current)}</span>
                  <span>${fmt(goal.target)}</span>
                </div>
                {(goal.name === 'FIRE Target' || goal.name === 'Emergency Fund') && (
                  <span className="inline-block px-2.5 py-1 bg-arc-accent-soft text-arc-accent-ink rounded-arc-sm text-[11px] font-semibold mt-1">
                    Auto-calculated
                  </span>
                )}
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Investment Portfolio by Category */}
        <Card hover={false}>
          <h3 className="text-h2 text-arc-ink mb-4">Investment Portfolio by Category</h3>
          <SafeChart chartsReady={chartsReady}>
            <GlowDonut
              data={investmentCategories}
              dataKey="amount"
              nameKey="name"
              valueFormatter={(v) => `$${Number(v).toLocaleString()}`}
            />
          </SafeChart>
          <div className="mt-4 text-center">
            <p className="text-sm text-arc-ink-3">
              Total Portfolio: <span className="font-semibold tnum text-arc-accent-ink">${fmt(totalInvestments)}</span>
            </p>
          </div>
        </Card>

        {/* Debt Payoff Progress */}
        <Card hover={false}>
          <h3 className="text-h2 text-arc-ink mb-4">Debt Payoff Progress</h3>
          {debts.filter((d) => d.original > 0).length > 0 ? (
            <SafeChart chartsReady={chartsReady}>
              <GlowBars
                data={debts.filter((d) => d.original > 0)}
                dataKey="paidOff"
                xKey="name"
                xAxisProps={{ angle: -45, textAnchor: 'end', height: 80 }}
                yTickFormatter={(value) => `${value.toFixed(0)}%`}
                valueFormatter={(v) => `${Number(v).toFixed(1)}%`}
                cellTone={(entry) => (entry.paidOff >= 100 ? 'pos' : 'gold')}
              />
            </SafeChart>
          ) : (
            <div className="flex items-center justify-center h-[300px] bg-arc-pos-soft rounded-arc-ctl">
              <div className="text-center">
                <PartyPopper className="w-12 h-12 mx-auto mb-4 text-arc-pos" />
                <h4 className="text-lg font-semibold text-arc-ink mb-2">Debt Free!</h4>
                <p className="text-sm text-arc-ink-3">You have no outstanding debts</p>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Quick Actions */}
      <Card hover={false}>
        <h3 className="text-h2 text-arc-ink mb-4">Quick Insights &amp; Next Steps</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Investment Insight */}
          <div className="p-4 bg-arc-accent-soft rounded-arc-ctl">
            <h4 className="font-semibold text-arc-ink mb-2 flex items-center gap-2">
              <PiggyBank size={16} className="text-arc-accent-ink" /> Investment Focus
            </h4>
            <p className="text-sm text-arc-ink-3">
              {emergencyFundProgress < 100
                ? `Build emergency fund first. Need $${fmt(emergencyFundTarget - emergencyFundFromInvestments)} more.`
                : totalInvestments < 100000
                ? "Emergency fund complete! Focus on growing investments to $100k milestone."
                : "Great progress! Consider diversifying across different categories."
              }
            </p>
          </div>

          {/* Debt Insight */}
          <div className="p-4 bg-arc-neg-soft rounded-arc-ctl">
            <h4 className="font-semibold text-arc-ink mb-2 flex items-center gap-2">
              <CreditCard size={16} className="text-arc-neg" /> Debt Strategy
            </h4>
            <p className="text-sm text-arc-ink-3">
              {totalDebts === 0
                ? "Excellent! You're debt-free. Redirect payments to investments."
                : totalDebts > totalInvestments
                ? "Consider prioritizing high-interest debt before investing more."
                : "Good balance. Continue paying down debt while building wealth."
              }
            </p>
          </div>

          {/* Net Worth Insight */}
          <div className="p-4 bg-arc-pos-soft rounded-arc-ctl">
            <h4 className="font-semibold text-arc-ink mb-2 flex items-center gap-2">
              <TrendingUp size={16} className="text-arc-pos" /> Wealth Building
            </h4>
            <p className="text-sm text-arc-ink-3">
              {autoNetWorth < 0
                ? "Focus on debt reduction and emergency fund building first."
                : autoNetWorth < 100000
                ? `You're ${((100000 - autoNetWorth) / 1000).toFixed(0)}k away from six-figure net worth!`
                : autoNetWorth < 1000000
                ? `You're ${((1000000 - autoNetWorth) / 1000).toFixed(0)}k away from millionaire status!`
                : "Congratulations on reaching millionaire status!"
              }
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default OverviewTab;